import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/context/AuthContext';

export const AnnouncementsSection: React.FC = () => {
    const { i18n } = useTranslation();
    const { user } = useAuth();
    const [announcements, setAnnouncements] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            if (!user) return;
            setLoading(true);

            try {
                // Check if admin
                const idTokenResult = await user.getIdTokenResult();
                const isAdmin = idTokenResult.claims.role === 'admin';

                // 1. Fetch guest data (if it exists)
                const guestDoc = await getDoc(doc(db, 'guests', user.uid));
                const gData = guestDoc.exists() ? guestDoc.data() : { events: [] };

                // 2. Fetch published announcements
                const q = query(
                    collection(db, 'announcements'),
                    where('isPublished', '==', true),
                    orderBy('publishedAt', 'desc'),
                    limit(5)
                );
                const sn = await getDocs(q);

                const filtered = sn.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter((a: any) => {
                        if (isAdmin) return true; // Admins see everything

                        // Event cross-check
                        const guestEvents = gData.events || [];
                        const announcementEvents = a.events || [];
                        return guestEvents.some((c: string) => announcementEvents.includes(c));
                    });

                setAnnouncements(filtered);
            } catch (err) {
                console.error("Error fetching announcements:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAnnouncements();
    }, [user]);

    if (loading || announcements.length === 0) return null;

    const lang = i18n.language.startsWith('es') ? 'es' : 'en';

    return (
        <section className="py-20 bg-primary/5">
            <div className="container mx-auto px-4 max-w-4xl">
                <header className="mb-12 text-center">
                    <h2 className="text-3xl md:text-4xl font-serif text-secondary mb-4 tracking-tight">
                        {lang === 'es' ? 'Anuncios' : 'Latest Updates'}
                    </h2>
                    <div className="w-16 h-1 bg-primary/30 mx-auto rounded-full" />
                </header>

                <div className="space-y-6">
                    {announcements.map((a) => (
                        <div key={a.id} className="p-8 bg-white rounded-3xl shadow-card border border-primary/10 transition-all hover:scale-[1.01]">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-secondary tracking-tight">
                                    {a.title?.[lang] || a.title?.en}
                                </h3>
                                <span className="text-[10px] font-black uppercase tracking-widest text-accent/60 bg-accent/5 px-2 py-1 rounded">
                                    {a.publishedAt?.toDate ? a.publishedAt.toDate().toLocaleDateString() : ''}
                                </span>
                            </div>
                            <p className="text-secondary-light leading-relaxed whitespace-pre-wrap">
                                {a.body?.[lang] || a.body?.en}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
